/**
 * usePropertySearchResults — the property search's results and which of the
 * three states the dropdown is in.
 *
 * Extracted from `PropertySearchList` so the drawer can answer keyboard input
 * without the list handing its state upward: arrow keys are pressed in the
 * SEARCH INPUT, which the drawer owns, while the results were fetched in the
 * list. Both call this; react-query dedupes, so the second caller costs no
 * request.
 */

import { useMemo } from 'react'
import { useMediaQuery } from '@mui/material'

import { MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { RealEstatePropertyListItemDTO } from 'models/property'
import { useGetSearchProperty } from 'dataQueries/property.query'

import { usePfEffectiveWorkspaceId } from './usePfEffectiveWorkspaceId'

export interface PropertySearchResults {
  /** The rows the dropdown renders, in render order. Empty when it renders none. */
  properties: RealEstatePropertyListItemDTO[]
  /** The dropdown is open with rows in it. */
  showResults: boolean
  /** A search was made and the dropdown has nothing to show for it. */
  showEmpty: boolean
  /** The box is focused but empty, and there is nothing recent to offer. */
  showEmptyFocusHint: boolean
}

export const usePropertySearchResults = (
  searchText: string,
  isSearchFocused: boolean
): PropertySearchResults => {
  // Admin acting for a client searches the CLIENT's workspace — reading the
  // acting user's own workspace left `enabled: !!workspaceId` false forever.
  const workspaceId = usePfEffectiveWorkspaceId()
  const isMobile = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  // `asClient`: this endpoint only serves the client representation, so an
  // admin's own (admin) mimetype 406s — see PropertyRepresentationOptions.
  const getSearchProperty = useGetSearchProperty(workspaceId, { searchText }, { asClient: true })
  const searchPropertyData = getSearchProperty.data?.data

  const searchPropertyContent = useMemo(
    () => searchPropertyData?.content?.filter((property) => property.address !== null),
    [searchPropertyData]
  )

  const isEmptySearch = !searchText && isSearchFocused
  const initialResultLimit = isMobile ? 3 : 5

  const properties = useMemo(
    () => (isEmptySearch
      ? searchPropertyContent?.slice(0, initialResultLimit)
      : searchPropertyContent) ?? [],
    [searchPropertyContent, isEmptySearch, initialResultLimit]
  )

  return {
    properties,
    showResults: !!(searchText || isEmptySearch) && getSearchProperty.isSuccess && properties.length > 0,
    // Keyed on the SAME list the results are keyed on, not on the page's own
    // `empty` flag. The two disagree whenever every match lacks an address: the
    // page says it found rows, the filter removes them all, and the dropdown
    // rendered NOTHING — no list and no explanation.
    showEmpty: !!searchText && getSearchProperty.isSuccess && properties.length === 0,
    showEmptyFocusHint: isEmptySearch && properties.length === 0,
  }
}
